import React, { useEffect } from "react";
import {
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormHelperText,
  Grid,
  Autocomplete,
  MenuItem,
  InputAdornment,
  Box,
} from "@mui/material";
import countries from "../../countries.json";

/**
 * A reusable form field component for edit registration that renders different input types based on the field configuration
 */
const EditFormField = ({
  rhf,
  field,
  setValue,
  showFamilyMembers = false,
  selectedCountry,
  setSelectedCountry,
  selectedMobileCode,
  setSelectedMobileCode,
  findCountryCodeByDialCode,
  setEmail,
  isEmailTaken,
  emailCheckLoading,
  parsedRegistration,
  pmt,
  promoDiscount = 0,
  memberDiscount = 0,
  isMemberIdValid = false,
  setOriginalPrice,
  setPrice,
  defaultValue,
}) => {
  const f = field;
  const errorMessage = rhf?.fieldState?.error?.message;

  // Set field value from registration data if available
  useEffect(() => {
    if (defaultValue !== undefined) {
      setValue(rhf.field.name, defaultValue);
    }
  }, [setValue, rhf.field.name, defaultValue]);

  // Email field change handler
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    if (rhf?.field?.onChange) {
      rhf.field.onChange(newEmail);
    }
    if (setEmail) setEmail(newEmail);
  };

  // Payment field change handler
  const handlePaymentChange = (e) => {
    if (rhf?.field?.onChange) {
      rhf.field.onChange(e.target.value);
    }
  };

  // Render different field types
  if (!field) {
    console.warn("EditFormField received undefined field prop");
    return null;
  }

  if (!f || !f.type) {
    return null;
  }

  // Multiple choice field
  if (f.type === "mcq") {
    return (
      <FormControl
        fullWidth
        required={f.required}
        sx={showFamilyMembers ? {} : f.sx || {}}
        error={!!rhf.fieldState?.error}
      >
        <FormLabel>{f.label || f.name}</FormLabel>
        <FormGroup>
          <Grid container spacing={1}>
            {f.options && Array.isArray(f.options)
              ? f.options
                  .map((op) => op.trim())
                  .map((op, i) => (
                    <Grid item xs={f.xs || 12} md={f.md || 6} key={i}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name={op}
                            checked={rhf.field.value?.includes(op)}
                            onChange={(e) => {
                              const arr = rhf.field.value || [];
                              if (e.target.checked) {
                                arr.push(op);
                              } else if (arr.includes(op)) {
                                arr.splice(arr.indexOf(op), 1);
                              }
                              rhf.field.onChange(arr);
                            }}
                          />
                        }
                        label={
                          op === "$Other" ? (
                            "Other:"
                          ) : (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: op,
                              }}
                            />
                          )
                        }
                      />
                    </Grid>
                  ))
              : null}
          </Grid>
        </FormGroup>
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
      </FormControl>
    );
  }

  // Radio field
  if (f.type === "radio") {
    return (
      <FormControl
        fullWidth
        required={f.required}
        sx={showFamilyMembers ? {} : f.sx || {}}
        error={!!rhf.fieldState?.error}
      >
        <FormLabel>{f.label || f.name}</FormLabel>
        <RadioGroup
          name={f.name}
          onChange={rhf.field.onChange}
          onBlur={rhf.field.onBlur}
          value={rhf.field.value}
        >
          <Grid container spacing={1}>
            {f.options && Array.isArray(f.options)
              ? f.options.map((op, i) => (
                  <Grid item xs={f.xs || 12} md={f.md || 6} key={i}>
                    <FormControlLabel
                      control={<Radio />}
                      label={op === "$Other" ? "Other:" : op}
                      value={op}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </RadioGroup>
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
      </FormControl>
    );
  }

  // Payment field
  if (f.type === "payment") {
    return (
      <FormControl
        fullWidth
        required={f.required}
        sx={showFamilyMembers ? {} : f.sx || {}}
        error={!!rhf.fieldState?.error}
      >
        <FormLabel>{f.label || f.name}</FormLabel>
        <RadioGroup
          name={f.name}
          onChange={(e) => handlePaymentChange(e)}
          onBlur={rhf.field.onBlur}
          value={rhf.field.value || ""}
        >
          <Grid container spacing={1}>
            {f.options && Array.isArray(f.options)
              ? f.options.map((op, i) => (
                  <Grid item xs={f.xs || 12} md={f.md || 6} key={i}>
                    <FormControlLabel
                      control={<Radio />}
                      label={op === "$Other" ? "Other:" : op}
                      value={op}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </RadioGroup>
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
      </FormControl>
    );
  }

  // HTML field (static content)
  if (f.type === "html") {
    return (
      <div
        style={showFamilyMembers ? {} : f.sx || {}}
        dangerouslySetInnerHTML={{ __html: f.defaultValue || "" }}
      />
    );
  }

  // Checkbox field
  if (f.type === "checkbox") {
    return (
      <FormControl
        required={f.required}
        fullWidth
        sx={showFamilyMembers ? {} : f.sx || {}}
        error={!!rhf.fieldState?.error}
      >
        <FormControlLabel
          onChange={rhf.field.onChange}
          onBlur={rhf.field.onBlur}
          inputRef={rhf.field.ref}
          control={<Checkbox />}
          label={f.label || f.name}
          name={rhf.field.name}
          checked={!!rhf.field.value}
        />
        {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
      </FormControl>
    );
  }

  // Select field (dropdown)
  if (f.type === "select") {
    return (
      <TextField
        name={rhf.field.name}
        value={rhf.field.value || ""}
        onChange={rhf.field.onChange}
        onBlur={rhf.field.onBlur}
        inputRef={rhf.field.ref}
        error={!!rhf.fieldState?.error}
        helperText={errorMessage}
        label={f.label || f.name}
        required={f.required}
        sx={showFamilyMembers ? {} : f.sx || {}}
        fullWidth
        select
      >
        <MenuItem value="">None</MenuItem>
        {f.options && Array.isArray(f.options)
          ? f.options.map((op) => (
              <MenuItem key={op} value={op}>
                {op === "$Other" ? "Other:" : op}
              </MenuItem>
            ))
          : null}
      </TextField>
    );
  }

  // Email field
  if (f.type === "email") {
    return (
      <TextField
        type="email"
        InputProps={{
          autoComplete:
            f.autoComplete || f.type === "confirm_email"
              ? "new-password"
              : "on",
          readOnly: f.readonly,
        }}
        name={rhf.field.name}
        value={rhf.field.value || ""}
        onChange={handleEmailChange}
        onBlur={rhf.field.onBlur}
        inputRef={rhf.field.ref}
        error={
          !!rhf.fieldState?.error ||
          (typeof isEmailTaken === "string" &&
            isEmailTaken.toLowerCase().includes("already registered"))
        }
        helperText={
          emailCheckLoading
            ? "Checking email..."
            : errorMessage
        }
        label={f.label || f.name}
        placeholder={f.placeholder}
        required={f.required}
        sx={showFamilyMembers ? {} : f.sx || {}}
        fullWidth
      />
    );
  }

  // COUNTRY / ISD CODE / MOBILE (owner 2026-08-15).
  //
  // These three types had NO branch here, so they fell through to the plain text input below —
  // an edit link rendered Country and ISD Code as empty text boxes instead of pickers, and an
  // invitee could type anything (or nothing) into a required field. The registration form has
  // always rendered them properly; only this edit renderer was missing them.
  if (f.type === "country") {
    return (
      <Autocomplete
        options={countries}
        autoHighlight
        // Existing value wins; a blank falls back to Oman, matching the registration form.
        value={
          !rhf.field.value
            ? countries.find((c) => c.code === "OM")
            : countries.find((c) => c.code === rhf.field.value) || null
        }
        disabled={!!f.readonly}
        onChange={(e, v) => {
          rhf.field.onChange(v?.code || "");
          // Keep the dial code in step with the country, as the registration form does.
          if (setValue) setValue("calling_code", v?.dial_code || "");
        }}
        onBlur={rhf.field.onBlur}
        getOptionLabel={(o) => o?.label || ""}
        renderOption={(props, option) => (
          <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
            <img
              loading="lazy"
              width="30"
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              alt=""
            />
            {option.label}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={f.label}
            required={!!f.required}
            placeholder={f.placeholder}
            name={rhf.field.name}
            inputRef={rhf.field.ref}
            error={!!rhf.fieldState.error}
            helperText={errorMessage}
            inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
          />
        )}
      />
    );
  }

  if (f.type === "mobile_code") {
    return (
      <Autocomplete
        options={countries}
        autoHighlight
        value={
          !rhf.field.value
            ? countries.find((c) => c.dial_code === "+968")
            : countries.find((c) => c.dial_code === rhf.field.value) || null
        }
        disabled={!!f.readonly}
        onChange={(e, v) => rhf.field.onChange(v?.dial_code || "")}
        onBlur={rhf.field.onBlur}
        getOptionLabel={(o) => o?.dial_code || ""}
        renderOption={(props, option) => (
          <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
            <img
              loading="lazy"
              width="30"
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              alt=""
            />
            {option.dial_code}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={f.label}
            required={!!f.required}
            name={rhf.field.name}
            inputRef={rhf.field.ref}
            error={!!rhf.fieldState.error}
            helperText={errorMessage}
            inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
          />
        )}
      />
    );
  }

  if (f.type === "mobile") {
    // Digits only — a mobile field that accepts letters produces unusable contact data.
    return (
      <TextField
        type="tel"
        label={f.label}
        required={!!f.required}
        placeholder={f.placeholder}
        name={rhf.field.name}
        inputRef={rhf.field.ref}
        value={rhf.field.value || ""}
        onChange={(e) => rhf.field.onChange(e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={rhf.field.onBlur}
        error={!!rhf.fieldState.error}
        helperText={errorMessage}
        InputProps={{ readOnly: !!f.readonly, autoComplete: "on" }}
        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
        fullWidth
      />
    );
  }

  // Default field (text, number, etc.)
  return (
    <TextField
      type={f.type === "number" ? "number" : "text"}
      InputProps={{
        autoComplete:
          f.autoComplete || f.type === "confirm_email" ? "new-password" : "on",
        readOnly: f.readonly,
      }}
      name={rhf.field.name}
      value={rhf.field.value || ""}
      onChange={rhf.field.onChange}
      onBlur={rhf.field.onBlur}
      inputRef={rhf.field.ref}
      error={!!rhf.fieldState?.error}
      helperText={errorMessage}
      label={f.label || f.name}
      placeholder={f.placeholder}
      required={f.required}
      sx={showFamilyMembers ? {} : f.sx || {}}
      fullWidth
    />
  );
};

export default EditFormField;
